import type { ParsedCSV } from '../../../../types/extraction';
import { sanitizeValue } from '../../../../utils/data-sanitizer';

/**
 * How many players on an uploaded roster carried a mailing address, and who did not.
 *
 * Read off the MAPPED roster rows, not off the API's response: the import endpoint reports how
 * many prospects it created, never which of them gained an address, and asking it to would mean a
 * per-player result payload for a number the client already knows. The trade-off is that this
 * counts rows on the roster rather than rows the server committed, so a row dropped server-side as
 * a same-team duplicate is still counted here. That is why the wording in the UI is about the
 * roster ("players on this roster"), not about what was written.
 */
export type RosterAddressSummary = {
  /** Named rows on the roster — the ones an import would send. */
  total: number;
  /** Of those, how many carried an address. */
  withAddress: number;
  /** Display names of the rows that did not, in roster order. */
  missingNames: string[];
};

/** How many names to name before falling back to a count. Keeps the success step readable. */
export const MAX_LISTED_MISSING_NAMES = 8;

function displayName(row: Record<string, string | number>): string {
  const first = sanitizeValue(row.first_name);
  const last = sanitizeValue(row.last_name);
  return [first, last].filter(Boolean).join(' ');
}

/**
 * Summarize address capture for one uploaded roster.
 *
 * Returns null when there is nothing to say — no rows, or no address column in the mapping at all.
 * A roster that never had an address column is the ordinary case and must not grow a "0 of 18"
 * line on every upload; a roster that HAS the column and read nothing out of it is worth showing,
 * because that is the case a coach would otherwise never learn about.
 */
export function summarizeRosterAddresses(data: ParsedCSV | null): RosterAddressSummary | null {
  if (!data || !data.headers.includes('address')) return null;

  let total = 0;
  let withAddress = 0;
  const missingNames: string[] = [];

  for (const row of data.rows) {
    const name = displayName(row);
    // Unnamed rows are dropped by the import itself, so counting them here would report a gap
    // against players nobody imported.
    if (!name) continue;

    total += 1;
    if (sanitizeValue(row.address)) {
      withAddress += 1;
    } else {
      missingNames.push(name);
    }
  }

  return total === 0 ? null : { total, withAddress, missingNames };
}

/**
 * Options describing what actually landed, so the copy cannot overclaim.
 */
export type DescribeOptions = {
  /**
   * False when part of the import failed (a team that errored out). The readout then says the
   * addresses reached the records that imported, rather than claiming all of them were saved.
   */
  allImported?: boolean;
};

/**
 * The one-or-two sentences the success step shows. Built here rather than in the component so the
 * web and mobile readouts say the same thing, and so the "nothing was read" and "part of the
 * import failed" cases cannot quietly turn into a claim that is not true.
 */
export function describeRosterAddresses(
  summary: RosterAddressSummary,
  { allImported = true }: DescribeOptions = {},
): string[] {
  const players = `${summary.total} player${summary.total === 1 ? '' : 's'}`;

  if (summary.withAddress === 0) {
    // The roster HAD an address column and nothing came out of it — the case a coach would
    // otherwise never learn about, and the one worth a sentence of its own.
    return [`No mailing addresses were read from this roster, for any of its ${players}.`];
  }

  const saved = allImported
    ? `now saved to their record${summary.withAddress === 1 ? '' : 's'}`
    : 'saved to the records that imported';
  const lines = [
    `${summary.withAddress} of ${players} on this roster had a mailing address, ${saved}.`,
  ];

  if (summary.missingNames.length > 0) {
    const listed =
      summary.missingNames.length > MAX_LISTED_MISSING_NAMES
        ? `${summary.missingNames.slice(0, MAX_LISTED_MISSING_NAMES).join(', ')} and ` +
          `${summary.missingNames.length - MAX_LISTED_MISSING_NAMES} more`
        : summary.missingNames.join(', ');
    lines.push(`No address on the roster for ${listed}.`);
  }

  return lines;
}
